import RelationshipCacheManager from '../utils/relationshipCacheManager.js';

/**
 * Relationship event service for VA Sprouts
 * 
 * Local EventEmitter pattern — no Supabase Realtime channels.
 * Consumers call triggerRelationshipChange() after mutations;
 * listeners registered via onRelationshipChange() are notified synchronously.
 * This eliminates 3 global Supabase channels that previously consumed WAL
 * replication slots and RAM on every page load.
 */
class RealtimeRelationshipService {
  constructor() {
    this.eventHandlers = new Map();
    this.isInitialized = false;
  }

  /**
   * Initialize the service (no-op — kept for API compatibility)
   */
  initialize() {
    if (this.isInitialized) return;
    this.isInitialized = true;
  }

  /**
   * Trigger a relationship change and notify all local listeners.
   * Call this after a job application or contract mutation succeeds.
   */
  async triggerRelationshipChange(vaId, employerId, type, action, metadata = {}) {
    const data = { vaId, employerId, type, action, timestamp: new Date().toISOString(), ...metadata };

    // Invalidate local cache
    try {
      if (type === 'job_application' && metadata.jobId) {
        await RelationshipCacheManager.onJobApplication(vaId, metadata.jobId);
      } else if (type === 'contract') {
        await RelationshipCacheManager.onContractCreated(vaId, employerId);
      }    } catch (err) {
      console.error('Cache invalidation error:', err);
    }

    // Notify local handlers
    this.triggerEventHandlers('relationship_changed', data);
  }

  /**
   * Register an event handler for relationship changes
   * @returns {string} Handler ID for removal
   */
  onRelationshipChange(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Map());
    }
    const handlerId = `handler_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.eventHandlers.get(event).set(handlerId, handler);
    return handlerId;
  }

  /**
   * Remove an event handler
   */
  removeEventHandler(event, handlerId) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).delete(handlerId);
    }
  }

  /**
   * Trigger event handlers for a specific event
   */
  triggerEventHandlers(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('Error in relationship event handler:', error);
        }
      });
    }
  }

  getStats() {
    return {
      isInitialized: this.isInitialized,
      activeSubscriptions: 0,
      registeredHandlers: Array.from(this.eventHandlers.entries()).reduce(
        (total, [, handlers]) => total + handlers.size, 0
      ),
      subscriptionTopics: []
    };
  }

  cleanup() {
    this.eventHandlers.clear();
    this.isInitialized = false;
  }

  isHealthy() {
    return this.isInitialized;
  }
}

// Create singleton instance
const realtimeRelationshipService = new RealtimeRelationshipService();

export default realtimeRelationshipService;
